import type FilterDate from '@/Enums/FilterDate.enum';
import FilterSelectLabel from '@/Enums/Filters/FilterSelectLabel.enum';
import type { IFootTrafficCampaignControlOption } from '@/Interfaces/Attribution/State.interface';
import type {
  IClientControlOption,
  IControlOption,
  IWhitelabelControlOption,
} from '@/Interfaces/ControlOption.interface';
import FootTrafficService from '@/Services/FootTraffic/FootTraffic.service';
import DateRangeWithShortcuts from '@/components/DateRangeWithShortcuts';
import FilterSelect from '@/components/Filter/FilterSelects/FilterSelect';
import MegaphoneIcon from '@/components/Icons/MegaphoneIcon';
import ShieldCheckIcon from '@/components/Icons/ShieldCheckIcon';
import TvIcon from '@/components/Icons/TvIcon';
import UserIcon from '@/components/Icons/UserIcon';
import { getFootTrafficShortcutItems } from '@/helpers/datePicker/shortcuts';
import type { GetOptionDisabled } from '@/types/Controls/TabSelect.types';
import type { ISODateString } from '@/types/DateTime.type';
import type { DateRange } from '@mui/x-date-pickers-pro';
import moment, { type Moment } from 'moment/moment';
import { useCallback, useMemo, useRef } from 'react';

interface IProps {
  dateStart: ISODateString;
  dateEnd: ISODateString;
  dateRangeFormat: FilterDate;
  setDateRange: (value: DateRange<Moment>, format?: FilterDate) => void;

  whitelabels: IWhitelabelControlOption[];
  whitelabelsDisabled: boolean;
  hideWhitelabels: boolean;
  filterWhitelabels: IWhitelabelControlOption[];
  onWhitelabelsChange: (whitelabels: IWhitelabelControlOption[]) => void;

  hideClients: boolean;
  clientsDisabled: boolean;
  clients: IClientControlOption[];
  filterClients: IClientControlOption[];
  onClientsChange: (clients: IClientControlOption[]) => void;

  advertisers: IControlOption[];
  filterAdvertisers: IControlOption[];
  onAdvertisersChange: (advertisers: IControlOption[]) => void;

  campaigns: IFootTrafficCampaignControlOption[];
  filterCampaigns: IFootTrafficCampaignControlOption[];
  onCampaignsChange: (campaigns: IFootTrafficCampaignControlOption[]) => void;
  loadingCampaigns: boolean;
}

const FilterSelects = ({
  dateStart,
  dateEnd,
  dateRangeFormat,
  setDateRange,

  whitelabels,
  hideWhitelabels,
  whitelabelsDisabled,
  filterWhitelabels,
  onWhitelabelsChange,

  hideClients,
  clientsDisabled,
  clients,
  filterClients,
  onClientsChange,

  advertisers,
  filterAdvertisers,
  onAdvertisersChange,

  campaigns,
  filterCampaigns,
  onCampaignsChange,
  loadingCampaigns,
}: IProps) => {
  const whitelabelsSelectRef = useRef<HTMLDivElement>(null);
  const clientSelectRef = useRef<HTMLDivElement>(null);
  const advertiserSelectRef = useRef<HTMLDivElement>(null);
  const campaignSelectRef = useRef<HTMLDivElement>(null);

  const range = useMemo(() => [moment(dateStart), moment(dateEnd)] as DateRange<Moment>, [dateStart, dateEnd]);
  const maxDate = useMemo(() => FootTrafficService.getMaxFootTrafficDate(), []);
  const shortcuts = useMemo(() => getFootTrafficShortcutItems(maxDate), [maxDate]);

  const getAdvertiserOptionDisabled = useCallback<GetOptionDisabled<IControlOption>>((_option, value, checked) => {
    if (!checked && value?.length >= 1) {
      return true;
    }
    return false;
  }, []);

  return (
    <div className='flex justify-between gap-3 sticky top-5 z-20 bg-white'>
      <div className='flex-1'>
        <DateRangeWithShortcuts
          value={range}
          dateRangeFormat={dateRangeFormat}
          onChange={setDateRange}
          disableFuture
          shortcuts={shortcuts}
          maxDate={maxDate}
        />
      </div>
      {hideWhitelabels ? null : (
        <div className='flex-1'>
          <FilterSelect<IWhitelabelControlOption>
            options={whitelabels}
            value={filterWhitelabels}
            onChange={onWhitelabelsChange}
            disabled={whitelabelsDisabled}
            selectRef={whitelabelsSelectRef}
            label={FilterSelectLabel.Whitelabel}
            StartAdornment={<ShieldCheckIcon className='shrink-0' />}
          />
        </div>
      )}

      {hideClients ? null : (
        <div className='flex-1'>
          <FilterSelect<IClientControlOption>
            options={clients}
            value={filterClients}
            onChange={onClientsChange}
            disabled={clientsDisabled}
            selectRef={clientSelectRef}
            label={FilterSelectLabel.Client}
            StartAdornment={<TvIcon className='shrink-0' />}
          />
        </div>
      )}

      <div className='flex-1'>
        <FilterSelect
          options={advertisers}
          value={filterAdvertisers}
          onChange={onAdvertisersChange}
          disabled={clientsDisabled || !filterClients.length}
          selectRef={advertiserSelectRef}
          label={FilterSelectLabel.Advertiser}
          StartAdornment={<UserIcon className='shrink-0' />}
          withAllOption={false}
          getOptionDisabled={getAdvertiserOptionDisabled}
        />
      </div>
      <div className='flex-1'>
        <FilterSelect
          options={campaigns}
          value={filterCampaigns}
          onChange={onCampaignsChange}
          disabled={!filterAdvertisers.length || loadingCampaigns}
          selectRef={campaignSelectRef}
          label={FilterSelectLabel.Campaign}
          StartAdornment={<MegaphoneIcon className='shrink-0' />}
          wideView
          wideViewFactor={2.5}
          withToggle
          grouped
        />
      </div>
    </div>
  );
};

export default FilterSelects;
